import {
  PAIR_AMOUNT_DECIMALS,
  USD_DECIMALS,
} from '@ui/constants/numbersDecimals';
import { getErc20TokenBySymbol } from '@ui/constants/tokens';
import { POSITION_BUFFER_THRESHOLD } from '@ui/constants/uiConfig';
import { useEntryPriceAfterSpread } from '@ui/features/Trade/hooks/useEntryPriceAfterSpread';
import { useFromInput } from '@ui/features/Trade/hooks/useFromInput';
import { useLiqPriceAfterSpread } from '@ui/features/Trade/hooks/useLiqPriceAfterSpread';
import { useSlippageValue } from '@ui/features/Trade/store/useSlippage';
import { useStopLossStore } from '@ui/features/Trade/store/useStopLossStore';
import { useTakeProfitStore } from '@ui/features/Trade/store/useTakeProfitStore';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { OpenMarketTradeParams } from '@ui/features/Trade/types';
import { checkOpenMarketTradeParamsEq } from '@ui/features/Trade/utils/checkOpenMarketTradeParamsEq';
import { isSlInvalid } from '@ui/features/Trade/utils/validateStoppLossPrice';
import { useDataChainId } from '@ui/hooks/datachain';
import { toBigValue } from '@ui/lib/bignumber';
import { usePriceUpdatesByPosition } from '@ui/services/backend/hooks/oracle/usePriceUpdatesByPosition';
import { usePythUpdateFee } from '@ui/services/pyth-hermes/pyth-price-update/usePythUpdateFee';
import { Address } from '@ui/types/alias';
import { getPriceLimit } from '@ui/utils/trade/getPriceLimit';
import { isNil } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';

import { usePayTokenPriceLatest } from '../../../hooks/usePayTokenPriceLatest';
import { usePositionSize } from '../../TradeModule/hooks/usePositionSize';
import { useCollateralToken } from '../../TradeModule/store/useCollateralToken';
import { useIsLong } from '../../TradeModule/store/useIsLong';
import { useIsLeverageMaxReached } from './useIsLeverageMaxReached';

type OpenMarketTradeParamsStore = {
  params: OpenMarketTradeParams | undefined;
  setParams: (params: OpenMarketTradeParams | undefined) => void;
}
const useOpenMarketTradeParamsStore = create<OpenMarketTradeParamsStore>((set) => ({
  params: undefined,
  setParams: (params) => set({ params }),
}));

export const useOpenMarketTradeParamsUpdate = () => {
  const selectedTradingPair = useTradingPair((s) => s.useTradingPair());
  const payToken = useCollateralToken((s) => s.useCollateralTokenValue());
  const inputAmount = useFromInput((s) => s.input).value.token;

  const payTokenPrice = usePayTokenPriceLatest(payToken);
  const currentInputUsd = useMemo(() => {
    if (!inputAmount || !payTokenPrice) return undefined;
    
return inputAmount.times(payTokenPrice);
  }, [inputAmount, payTokenPrice]);

  const positionSize = usePositionSize({
    inputUsd: currentInputUsd,
    isBeforeFee: false,
  });
  
  const tpPriceValue = useTakeProfitStore((s) => s.tpValue).priceValue;
  const slPriceValue = useStopLossStore((s) => s.slValue).priceValue;
  const isLong = useIsLong((s) => s._persistValue);
  const slippageValue = useSlippageValue((s) => s._persistValue);
  const liqPrice = useLiqPriceAfterSpread();
  const openMarketTradeParamsStore = useOpenMarketTradeParamsStore();
  const isLeverageReachMax = useIsLeverageMaxReached();
  const chainId = useDataChainId();
  const wrappedNativeToken = getErc20TokenBySymbol('WRAPPED_NATIVE', chainId);
  const collateralForOracle =
    payToken?.isNative && !isNil(wrappedNativeToken)
      ? wrappedNativeToken.address
      : payToken?.address;

  const { data: oracleBatchPrice } = usePriceUpdatesByPosition({
    pairBase: selectedTradingPair?.address,
    collateral: collateralForOracle,
    enabled: !!selectedTradingPair?.address && !!collateralForOracle,
  });

  const pythUpdateData = useMemo(
    () => (oracleBatchPrice?.pythPriceUpdateData ?? []) as Address[],
    [oracleBatchPrice?.pythPriceUpdateData],
  );
  const pythProUpdateData = useMemo(
    () => (oracleBatchPrice?.pythProPriceUpdateData ?? []) as Address[],
    [oracleBatchPrice?.pythProPriceUpdateData],
  );
  const oraclePythUpdateFee = oracleBatchPrice?.updateFee
    ? BigInt(oracleBatchPrice.updateFee)
    : undefined;
  const extraFee = oracleBatchPrice?.verifition_fee
    ? BigInt(oracleBatchPrice.verifition_fee)
    : 0n;

  const { data: fallbackPythUpdateFee } = usePythUpdateFee({
    pythUpdateData: pythUpdateData,
  });
  const pythUpdateFee = oraclePythUpdateFee ?? fallbackPythUpdateFee;

  const executionMarketPrice = useEntryPriceAfterSpread();

  useEffect(() => {
    if (
      isNil(inputAmount) ||
      inputAmount.isNaN() ||
      isNil(executionMarketPrice) ||
      isNil(payToken) ||
      isNil(positionSize) ||
      positionSize.isNaN() ||
      isNil(selectedTradingPair) ||
      isNil(liqPrice) ||
      isNil(pythUpdateFee) ||
      isNil(oracleBatchPrice)
    )
      return;

    const priceLimit = getPriceLimit({
      isLong,
      slippageValue, // user 's slippage value only apply here , only matter when submit to SC, do not need to display
      price: executionMarketPrice,
    });

    /**
     * According to APX, when the position size hits the max leverage of the current leverage margin
     * A 0.5% deduction is required to prevent a max leverage error.
     */
    const countInPositionSize = isLeverageReachMax
      ? positionSize.multipliedBy(1 - POSITION_BUFFER_THRESHOLD)
      : positionSize;

    // collateral token amount
    const amountIn = toBigValue(inputAmount, payToken.decimals);
    const tokenIn =
      payToken.isNative && !isNil(wrappedNativeToken)
        ? wrappedNativeToken.address
        : payToken.address;

    const newParams = {
      pairBase: selectedTradingPair.address,
      isLong: isLong,
      tokenIn,
      lvToken: payToken.isDirectCollateral ? tokenIn : payToken.lvTokenAddress,
      amountIn: amountIn,
      value: payToken.isNative
        ? amountIn + pythUpdateFee + extraFee
        : pythUpdateFee + extraFee,
      qty: toBigValue(
        countInPositionSize.div(executionMarketPrice),
        PAIR_AMOUNT_DECIMALS,
      ), //pair amount / positionQty, should be entryPrice instead of price limit
      price: toBigValue(priceLimit, USD_DECIMALS), // acceptable entry price
      stopLoss:
        isNaN(parseFloat(slPriceValue)) ||
          isSlInvalid(liqPrice, slPriceValue, isLong, executionMarketPrice)
          ? 0n
          : toBigValue(slPriceValue, USD_DECIMALS),
      takeProfit: isNaN(parseFloat(tpPriceValue))
        ? 0n
        : toBigValue(tpPriceValue, USD_DECIMALS), // in biz logic, default TP should not be 0, but still give this handling for safety
      broker: 1,
      pythUpdateData: pythUpdateData as Address[],
      oracleUpdateData: {
        pythPriceUpdateData: pythUpdateData,
        pythProPriceUpdateData: pythProUpdateData,
      },
      extraFee,
    };

    if (checkOpenMarketTradeParamsEq(
      openMarketTradeParamsStore.params,
      newParams
    )) {
      return;
    }
    openMarketTradeParamsStore.setParams(newParams);
  }, [
    chainId,
    inputAmount,
    executionMarketPrice,
    payToken,
    positionSize,
    selectedTradingPair,
    liqPrice,
    pythUpdateFee,
    extraFee,
    isLong,
    slippageValue,
    isLeverageReachMax,
    slPriceValue,
    tpPriceValue,
    pythUpdateData,
    pythProUpdateData,
    oracleBatchPrice,
    openMarketTradeParamsStore,
    wrappedNativeToken,
  ]);
};

export const useOpenMarketTradeParams = () => {
  const { params } = useOpenMarketTradeParamsStore();

  return params;
};